import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { type Href, Redirect, useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { useAtomValue } from 'jotai';
import { AlertCircleIcon } from 'lucide-react-native';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Animated, Easing } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';

import { AwarenessLogo } from '@/components/AwarenessLogo';
import { GoogleSignInButton } from '@/components/GoogleSignInButton';
import { CreateAccountReferralBanner } from '@/components/create-account/CreateAccountReferralBanner';
import { Header } from '@/components/header';
import PromotionalCarousel from '@/components/promotional-carousel';
import { getScreenColumnClasses } from '@/components/screenColumn';
import { SupportForm } from '@/components/supportForm';
import { Button, ButtonSpinner, ButtonText } from '@/components/ui/button';
import { Divider } from '@/components/ui/divider';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from '@/components/ui/form-control';
import { HStack } from '@/components/ui/hstack';
import { Input, InputField } from '@/components/ui/input';
import { Link, LinkText } from '@/components/ui/link';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import UnauthenticatedLayout from '@/components/unauthenticatedLayout';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { ROUTES } from '@/constants/routes';
import { useBackNavigation } from '@/hooks/useBackNavigation';
import { useColors } from '@/hooks/useColors';
import { useCreateAccount, createAccountSchema } from '@/hooks/useCreateAccount';
import { useRedirectPath, performRedirectNavigation } from '@/hooks/useRedirectPath';
import { useReferralNavigation } from '@/hooks/useReferralNavigation';
import { useToaster } from '@/hooks/useToaster';
import { startGoogleSignIn } from '@/services/auth';
import { checkEmailAvailable } from '@/services/checkEmailAvailable';
import { getCurrentUserScopedQueryOptions } from '@/services/getCurrentUserScoped';
import { getReferralByCodeV2 } from '@/services/getReferralByCodeV2';
import { authStateResolvedAtom, userIdAtom } from '@/services/globalState';
import { segmentClient } from '@/util/analytics';
import {
  trackAuthTriggered,
  trackCreateAccountReferredByBannerViewed,
  trackAccountConflictDetected,
  trackCreateAccountStep1Completed,
  trackCreateAccountClosed,
  trackSignupEmailPrefilled,
  trackSupportModalOpened,
} from '@/util/analyticsEvents';
import { getPlatformStorage } from '@/util/platformStorage';

const SCREEN_COLUMN = getScreenColumnClasses({ gutter: 'px-5 web:px-0' });

/**
 * Create Account – Step 1 (Email)
 * Responsibilities:
 * - Validate email with zod and persist to local store
 * - Check server-side availability before navigating to Step 2
 * - Track step completion and support modal opens
 */
function Content() {
  const { navigate } = useRouter();

  const toast = useToaster();

  const { data, setData } = useCreateAccount();

  const {
    referred_by: referredBy,
    utm_source: utmSource,
    email: emailParam,
    auth: authParam,
  } = useLocalSearchParams<{
    referred_by?: string;
    utm_source?: string;
    email?: string;
    auth?: 'pass' | 'google';
  }>();

  const hasTriggeredOAuth = useRef(false);

  const hasTrackedEmailPrefill = useRef(false);

  const normalizedEmailParam = emailParam?.trim().toLowerCase() ?? '';

  const normalizedReferralCode = referredBy?.trim() ?? '';

  const shouldPrefillEmail = Boolean(normalizedEmailParam) && (authParam === 'pass' || !authParam);

  const isFromMarketingSite = utmSource === 'ms';

  const { data: referralLookupData, isLoading: isReferralLookupLoading } = useQuery({
    queryKey: ['referralByCodeV2', normalizedReferralCode],
    queryFn: () => getReferralByCodeV2(normalizedReferralCode),
    enabled: normalizedReferralCode.length > 0,
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const trackedReferralBannerCodeRef = useRef<string | null>(null);

  const showReferralBanner =
    normalizedReferralCode.length > 0 && !!referralLookupData?.referrerDisplayName;

  const referralBannerOpacity = useRef(new Animated.Value(0)).current;

  const referralBannerTranslateY = useRef(new Animated.Value(-8)).current;

  const formOpacity = useRef(new Animated.Value(0)).current;

  const formTranslateY = useRef(new Animated.Value(10)).current;

  const hasPlayedIntroAnimation = useRef(false);

  useEffect(() => {
    if (!showReferralBanner) {
      referralBannerOpacity.setValue(0);

      referralBannerTranslateY.setValue(-8);

      return;
    }

    Animated.parallel([
      Animated.timing(referralBannerOpacity, {
        toValue: 1,
        duration: 260,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(referralBannerTranslateY, {
        toValue: 0,
        duration: 260,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
    ]).start();
  }, [showReferralBanner, referralBannerOpacity, referralBannerTranslateY]);

  useEffect(() => {
    if (hasPlayedIntroAnimation.current) {
      return;
    }

    if (normalizedReferralCode.length > 0 && isReferralLookupLoading) {
      return;
    }

    hasPlayedIntroAnimation.current = true;

    const delay = showReferralBanner ? 120 : 0;

    Animated.parallel([
      Animated.timing(formOpacity, {
        toValue: 1,
        duration: 320,
        delay,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(formTranslateY, {
        toValue: 0,
        duration: 320,
        delay,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
    ]).start();
  }, [
    formOpacity,
    formTranslateY,
    isReferralLookupLoading,
    normalizedReferralCode.length,
    showReferralBanner,
  ]);

  useFocusEffect(
    useCallback(() => {
      if (!showReferralBanner) {
        return;
      }

      const referralCode = referralLookupData?.code || normalizedReferralCode;

      if (trackedReferralBannerCodeRef.current === referralCode) {
        return;
      }

      trackedReferralBannerCodeRef.current = referralCode;

      trackCreateAccountReferredByBannerViewed({
        referralCode,
        signupStep: 'step_1',
        referrerId: referralLookupData?.referrerId,
        referrerName: referralLookupData?.referrerDisplayName,
      });
    }, [showReferralBanner, referralLookupData, normalizedReferralCode])
  );

  useEffect(() => {
    if (!referredBy) {
      return;
    }

    segmentClient.track(ANALYTICS_EVENTS.INVITE_LINK_VISITED, {
      referral_code: String(referredBy),
      utm_source: utmSource || null,
    });
  }, [referredBy, utmSource]);

  useEffect(() => {
    if (!referredBy) {
      return;
    }

    const code = String(referredBy);

    if (data?.referredBy !== code) {
      setData({ ...data, referredBy: code });
    }

    try {
      const storage = getPlatformStorage(undefined, 'local');

      storage.setItem('primum_referral_code', code).catch(() => {});
    } catch {}
  }, [referredBy, data, setData]);

  useEffect(() => {
    if (referredBy || data?.referredBy) {
      return;
    }

    const restoreReferralCode = async () => {
      try {
        const storage = getPlatformStorage(undefined, 'local');

        const storedCode = await storage.getItem('primum_referral_code');

        if (storedCode) {
          setData({ ...data, referredBy: storedCode });
        }
      } catch {}
    };

    restoreReferralCode();
  }, [referredBy, data, setData]);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setError,
    clearErrors,
    reset,
  } = useForm({
    defaultValues: {
      ...data,
      ...(shouldPrefillEmail ? { email: normalizedEmailParam } : {}),
    },
    resolver: zodResolver(createAccountSchema),
  });

  useEffect(() => {
    if (authParam !== 'google' || hasTriggeredOAuth.current) {
      return;
    }

    hasTriggeredOAuth.current = true;

    trackAuthTriggered({
      provider: 'google',
      source: 'marketing_site',
      referredBy: referredBy || null,
      utmSource: utmSource || null,
    });

    startGoogleSignIn('marketing_site', {
      loginHint: normalizedEmailParam || undefined,
    });
  }, [authParam, normalizedEmailParam, referredBy, utmSource]);

  useEffect(() => {
    if (!shouldPrefillEmail) {
      return;
    }

    if (isFromMarketingSite && !hasTrackedEmailPrefill.current && normalizedEmailParam) {
      hasTrackedEmailPrefill.current = true;

      trackSignupEmailPrefilled({
        referredBy: referredBy || null,
        utmSource: utmSource || null,
      });
    }

    if (data?.email !== normalizedEmailParam || (referredBy && data?.referredBy !== referredBy)) {
      const updatedData = {
        ...data,
        email: normalizedEmailParam,
        ...(referredBy ? { referredBy: String(referredBy) } : {}),
      };

      setData(updatedData);

      reset({ email: normalizedEmailParam });
    }
  }, [
    data,
    shouldPrefillEmail,
    normalizedEmailParam,
    referredBy,
    setData,
    reset,
    isFromMarketingSite,
    utmSource,
  ]);

  const [showSupportModal, setShowSupportModal] = useState(false);

  const { getNavigationPath } = useReferralNavigation();

  const { redirectPath, appendRedirectPath } = useRedirectPath();

  const onNext = handleSubmit(async (formValues) => {
    try {
      const email = (formValues.email || '').trim().toLowerCase();

      setData({
        ...data,
        email,
        ...(referredBy ? { referredBy: String(referredBy) } : {}),
      });

      const result = await checkEmailAvailable(email);

      if (result?.available === false) {
        const isGoogleAccount = result.authProvider === 'google';

        const inlineMessage = isGoogleAccount
          ? 'This email is registered with Google. Continue with Google to sign in.'
          : 'This email is already in use. Sign in to continue.';

        setError('email', { message: inlineMessage });

        toast.showError({ title: inlineMessage });

        trackAccountConflictDetected({
          conflictType: 'EMAIL_IN_USE',
          authProvider: isGoogleAccount ? 'google' : 'password',
          signupMethod: isGoogleAccount ? 'oauth' : 'password',
          source: 'create_account_step_1',
          email,
        });

        return;
      }

      trackCreateAccountStep1Completed({ email });

      const navigationPath = getNavigationPath('/create-account/step-2');

      const navigationPathWithRedirectUrl = appendRedirectPath(navigationPath as string);

      navigate(navigationPathWithRedirectUrl);
    } catch (error) {
      toast.showError({
        title: 'Could not complete request',
        apiError: {
          error,
          errorPatternMatcher: {},
        },
      });
    }
  });

  const { colors } = useColors();

  const { bottom } = useSafeAreaInsets();

  const emailValue = watch('email') || '';

  return (
    <VStack
      className={twJoin('flex-1', SCREEN_COLUMN.frame)}
      style={{
        paddingBottom: bottom,
      }}>
      {isWeb ? null : (
        <Header
          contentScrollsUnderHeader
          isBackButton
          title="Create Account"
          statusBarBackgroundColor={colors.background[0]}
        />
      )}

      <KeyboardAwareScrollView
        contentInsetAdjustmentBehavior="automatic"
        mode="layout"
        bottomOffset={12}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
        showsVerticalScrollIndicator={false}
        contentContainerClassName={twJoin(
          'flex-col flex-grow pt-3 pb-6 web:pt-6 web:pb-10',
          SCREEN_COLUMN.content
        )}>
        <CreateAccountReferralBanner
          isVisible={showReferralBanner}
          referralLookupData={referralLookupData}
          referralBannerOpacity={referralBannerOpacity}
          referralBannerTranslateY={referralBannerTranslateY}
          onPressReferrerProfile={(href) => navigate(href)}
        />

        {isWeb ? (
          <Text size="sm" className="mb-8 font-medium text-typography-900">
            Step 1 of 2
          </Text>
        ) : null}

        <VStack className="items-center web:items-start">
          <AwarenessLogo
            alt="Primum logo with mark"
            kind="dark2x"
            box={{ height: 35, width: 280, marginBottom: 8 }}
          />
          <Text
            size="md"
            className="mb-1 mt-6 text-center text-typography-900 web:mb-6 web:text-left">
            Please register to access Primum.
          </Text>
        </VStack>

        <VStack space="md" className="mb-2 mt-6 web:mb-8 web:mt-0">
          <Controller
            name="email"
            control={control}
            render={({ field: { onChange, value } }) => (
              <FormControl isInvalid={!!errors.email?.message}>
                <FormControlLabel className="mb-1.5">
                  <FormControlLabelText size="sm" className="font-medium text-typography-900">
                    Email address*
                  </FormControlLabelText>
                </FormControlLabel>

                <Input>
                  <InputField
                    placeholder="Enter email address"
                    placeholderTextColor={colors.typography[600]}
                    style={{ color: colors.typography[900] }}
                    className="text-base"
                    value={value}
                    onChangeText={(text: string) => {
                      if (errors.email?.message) {
                        clearErrors('email');
                      }

                      onChange(text.toLowerCase());
                    }}
                    autoCapitalize="none"
                    autoCorrect={false}
                    keyboardType="email-address"
                    enterKeyHint="next"
                    returnKeyType="next"
                    blurOnSubmit={false}
                    onSubmitEditing={onNext}
                  />
                </Input>

                <FormControlError className="mt-2 items-start">
                  <FormControlErrorIcon className="mt-0.5" size="xs" as={AlertCircleIcon} />
                  <FormControlErrorText size="xs">
                    {(() => {
                      const message = errors.email?.message?.toString() ?? '';

                      const isExistingAccount =
                        message.includes('already in use') ||
                        message.includes('registered with Google');

                      if (!isExistingAccount) {
                        return errors.email?.message;
                      }

                      return (
                        <>
                          {message} Need help?{' '}
                          <LinkText
                            onPress={() => {
                              setShowSupportModal(true);

                              trackSupportModalOpened({
                                step: 'step1',
                                trigger: 'email_error',
                              });
                            }}
                            size="xs"
                            className="text-typography-error data-[hover=true]:text-typography-error">
                            Contact Support
                          </LinkText>
                        </>
                      );
                    })()}
                  </FormControlErrorText>
                </FormControlError>
              </FormControl>
            )}
          />
        </VStack>

        <Button
          size="xl"
          className="mt-4 w-full gap-x-2 web:mt-6"
          onPress={onNext}
          disabled={isSubmitting}>
          {isSubmitting ? <ButtonSpinner color={colors.primary[200]} /> : null}
          <ButtonText size="md">Continue</ButtonText>
        </Button>

        <HStack className="mt-4 justify-start gap-x-1 web:mt-6">
          <Text size="sm" className="font-normal text-typography-900">
            Already have an account?
          </Text>

          <Link
            onPress={() => {
              const typedEmail = (watch('email') || '').trim();

              const target = typedEmail
                ? `/(open)/login?email=${encodeURIComponent(typedEmail)}`
                : '/(open)/login';

              navigate(appendRedirectPath(target));
            }}>
            <LinkText
              size="sm"
              className="font-bold text-primary-500 data-[hover=true]:text-primary-600">
              Sign in
            </LinkText>
          </Link>
        </HStack>

        <HStack className="mb-4 mt-6 flex-row items-center gap-x-2">
          <Divider className="flex-1" />

          <Text size="xs" className="text-typography-600">
            OR
          </Text>

          <Divider className="flex-1" />
        </HStack>

        <VStack className="mb-6 gap-3">
          <GoogleSignInButton
            onPress={() => {
              trackAuthTriggered({
                provider: 'google',
                source: 'create_account_step_1',
              });

              startGoogleSignIn(
                'create_account_step_1',
                redirectPath ? { redirectPath } : undefined
              );
            }}
          />
        </VStack>
      </KeyboardAwareScrollView>

      {showSupportModal && (
        <SupportForm
          isOpen={showSupportModal}
          onClose={() => setShowSupportModal(false)}
          initialValues={{ email: emailValue }}
        />
      )}
    </VStack>
  );
}

/**
 * Create Account Step 1 page component.
 *
 * Redirects authenticated users to directory. Returns null while auth state resolves
 * to prevent flash of create account screen.
 */
export default function Step1() {
  const { getDefaultRoute } = useReferralNavigation();

  const { redirectPath } = useRedirectPath();

  const backDestination = (redirectPath || getDefaultRoute()) as Href;

  const { navigateBack } = useBackNavigation(backDestination);

  const { replace } = useRouter();

  const userId = useAtomValue(userIdAtom);

  const authStateResolved = useAtomValue(authStateResolvedAtom);

  const { isLoading } = useQuery(getCurrentUserScopedQueryOptions({ userId }));

  if (!authStateResolved || isLoading) {
    return null;
  }

  if (userId) {
    return <Redirect href={ROUTES.DIRECTORY_TAB} />;
  }

  if (isWeb) {
    return (
      <UnauthenticatedLayout
        verticalAligned
        rightContent={<PromotionalCarousel onDark />}
        onBack={() => {
          trackCreateAccountClosed({ step: 'step1' });

          if (isWeb) {
            performRedirectNavigation(redirectPath, '/directory', replace);
          } else {
            navigateBack();
          }
        }}>
        <Content />
      </UnauthenticatedLayout>
    );
  }

  return <Content />;
}
